import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useRetrieveProjectResourceAllocations,
  useRemoveResourceFromProject,
  useProjectResourcesRetrieval,
} from "@planzone/view-models";
import { id, ProjectResource, Task } from "@planzone/dto";
import { Modal } from "@common/modal";
import { Button } from "@common/button";
import { Checkbox } from "@common/checkbox";
import { ResourceAvatar } from "@common/resourceAvatar";
import { VisibilityWatcher } from "@common/visibilityWatcher";
import { QuickAllocationMenu } from "@common/quickAllocationMenu";
import { List } from "./list";
import { Resource } from "./resource";
import { Context } from "./utils";
import { Identifiable } from "@planzone/dto";
import { ToggleArrow } from "@common/toggleArrow";
import { Props } from "./types";
import styles from "./styles.scss";
import classNames from "classnames";
import { ScreenWatcher } from "@common/screenWatcher";
import { LayoutWatcher } from "@common/layoutWatcher";

function RemoveResourceFromProject({
  project,
  projectResource,
  cancel,
}: Props) {
  const { t } = useTranslation();
  const { isRequestPending, removeResourceFromProject } =
    useRemoveResourceFromProject(project, projectResource);
  const {
    isRequestPending: isAssignmentsLoading,
    isRequestSuccess: areAssignmentsLoaded,
    isRequestFailure: hasAssignmentsFailed,
    isDirty,
    resourceAllocations,
    retrieveProjectResourceAllocations,
  } = useRetrieveProjectResourceAllocations(project, projectResource);
  const [hasRequestedAllocationTransfer, setHasRequestedAllocationTransfer] =
    React.useState(false);
  const { projectResources } = useProjectResourcesRetrieval(project);
  const [destinationResource, setDestinationResource] =
    React.useState<Identifiable<ProjectResource> | null>(null);
  const [selectedTasks, setSelectedTasks] = useState<Identifiable<Task>[]>([]);
  const [toggled, setToggled] = React.useState<{ [key: string]: boolean }>({});

  const open = (id: string) => setToggled((prev) => ({ ...prev, [id]: true }));
  const close = (id: string) =>
    setToggled((prev) => ({ ...prev, [id]: false }));

  const toggleTaskSelection = (task: Identifiable<Task>) => {
    setSelectedTasks((prev) => {
      const exists = prev.some((t) => t.uuid === task.uuid);
      if (exists) {
        return prev.filter((t) => t.uuid !== task.uuid);
      } else {
        return [...prev, task];
      }
    });
  };

  const getAllTasksFromList = (list: any): Identifiable<Task>[] => {
    let tasks: Identifiable<Task>[] = [...list.tasks];
    list.subLists.forEach((subList: any) => {
      tasks = [...tasks, ...getAllTasksFromList(subList)];
    });
    return tasks;
  };

  const toggleAllTasks = () => {
    if (!areAssignmentsLoaded || !resourceAllocations?.resource?.lists) return;
    const allTasks = resourceAllocations.resource.lists.flatMap((list) =>
      getAllTasksFromList(list),
    );
    const allSelected = allTasks.every((task) =>
      selectedTasks.some((t) => t.uuid === task.uuid),
    );

    allSelected ? setSelectedTasks([]) : setSelectedTasks(allTasks);
  };

  const toggleListTasks = (list: any) => {
    const listTasks = getAllTasksFromList(list);
    const allSelected = listTasks.every((task) =>
      selectedTasks.some((t) => t.uuid === task.uuid),
    );

    if (allSelected) {
      setSelectedTasks((prev) =>
        prev.filter((t) => !listTasks.some((task) => task.uuid === t.uuid)),
      );
    } else {
      setSelectedTasks((prev) => [
        ...prev.filter((t) => !listTasks.some((task) => task.uuid === t.uuid)),
        ...listTasks,
      ]);
    }
  };

  const hasTasks =
    areAssignmentsLoaded &&
    resourceAllocations?.resource?.lists?.length > 0 &&
    resourceAllocations.resource.lists.flatMap((list) =>
      getAllTasksFromList(list),
    ).length > 0;

  React.useEffect(() => {
    if (
      (!isAssignmentsLoading &&
        !areAssignmentsLoaded &&
        !hasAssignmentsFailed) ||
      (isDirty && !isAssignmentsLoading)
    ) {
      retrieveProjectResourceAllocations({ project, projectResource });
    }
  }, [project.uuid, isDirty, projectResource.uuid]);
  return (
    <Context.Provider value={{ toggled, open, close }}>
      <Modal header={t("removeResourceFromProject.title")} close={cancel}>
        <div className={styles.removeResourceFromProject}>
          <div className={styles.main}>
            <div className={styles.content}>
              <div className={styles.group}>
                <p className={styles.warning}>
                  {t("removeResourceFromProject.warning")}
                </p>
              </div>
              <div className={styles.group}>
                <Resource resource={projectResource.resource} />
              </div>
              {projectResource && (
                <label
                  className={styles.toggle}
                  onClick={() =>
                    setHasRequestedAllocationTransfer(
                      !hasRequestedAllocationTransfer,
                    )
                  }
                >
                  <Checkbox isChecked={hasRequestedAllocationTransfer} />
                  <p>{t("removeResourceFromProject.transfer")}</p>
                </label>
              )}
              {hasRequestedAllocationTransfer && (
                <div className={styles.group}>
                  <VisibilityWatcher>
                    {(isVisible, show, hide) => (
                      <div
                        className={classNames(styles.allocationWrapper, {
                          [styles.openedInput]: isVisible,
                        })}
                      >
                        <div
                          className={styles.input}
                          onClick={(e) => {
                            isVisible && e.stopPropagation();
                            isVisible && hide();
                            !isVisible && show();
                          }}
                        >
                          {destinationResource ? (
                            <React.Fragment>
                              <div className={styles.resourceAvatar}>
                                <ResourceAvatar
                                  resource={destinationResource.resource}
                                />
                              </div>
                              <p>{destinationResource.resource.name}</p>
                              <ToggleArrow className={styles.toggleIcon} />
                            </React.Fragment>
                          ) : (
                            <React.Fragment>
                              <p>
                                {t("removeResourceFromProject.collaborator")}
                              </p>
                              <ToggleArrow className={styles.toggleIcon} />
                            </React.Fragment>
                          )}
                        </div>
                        {isVisible && (
                          <QuickAllocationMenu
                            project={project}
                            className={styles.quickAllocationMenu}
                            filters={[projectResource.resource]}
                            selectResource={(resource) => {
                              const selectedProjectResource =
                                projectResources.find(
                                  (projectResource) =>
                                    projectResource.resource.uuid ===
                                    resource.uuid,
                                );
                              selectedProjectResource &&
                                setDestinationResource(selectedProjectResource);
                              hide();
                            }}
                          />
                        )}
                      </div>
                    )}
                  </VisibilityWatcher>
                  <div className={styles.listsContainer}>
                    {areAssignmentsLoaded && hasTasks && (
                      <div className={styles.selectAll}>
                        <label className={styles.toggle}>
                          <div
                            className={styles.checkbox}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleAllTasks();
                            }}
                          >
                            <Checkbox
                              isChecked={
                                resourceAllocations?.resource?.lists?.length >
                                  0 &&
                                resourceAllocations.resource.lists
                                  .flatMap((list) => getAllTasksFromList(list))
                                  .every((task) =>
                                    selectedTasks.some(
                                      (t) => t.uuid === task.uuid,
                                    ),
                                  )
                              }
                            />
                          </div>
                          <p>{t("removeResourceFromProject.selectAllTasks")}</p>
                        </label>
                      </div>
                    )}
                    <ScreenWatcher
                      renderChildren={({ height }) => (
                        <LayoutWatcher
                          renderChildren={({ top }) =>
                            areAssignmentsLoaded && (
                              <>
                                {!hasTasks ? (
                                  <p className={styles.noTasks}>
                                    {t("removeResourceFromProject.noTasks")}
                                  </p>
                                ) : (
                                  resourceAllocations?.resource?.lists?.map(
                                    (list) => (
                                      <div
                                        style={{
                                          maxHeight: height - top - 200,
                                          overflow: "auto",
                                        }}
                                      >
                                        <List
                                          key={list.uuid}
                                          list={list}
                                          project={project}
                                          resource={projectResource.resource}
                                          depth={0}
                                          isRoot
                                          selectedTask={selectedTasks}
                                          toggleTaskSelection={
                                            toggleTaskSelection
                                          }
                                          toggleListTasks={toggleListTasks}
                                        />
                                      </div>
                                    ),
                                  )
                                )}
                              </>
                            )
                          }
                        />
                      )}
                    />
                  </div>
                </div>
              )}
            </div>
            <p className={styles.warningTimeRecord}>
              {t("removeResourceFromProject.warningTimeRecord")}
            </p>
            <div className={styles.buttons}>
              <Button type="cancel" press={cancel}>
                {t("removeResourceFromProject.cancel")}
              </Button>
              <Button
                type="submit"
                status={isRequestPending ? "pending" : "none"}
                isDisabled={
                  isRequestPending ||
                  (hasRequestedAllocationTransfer && !destinationResource)
                }
                press={() =>
                  removeResourceFromProject({
                    project,
                    projectResource,
                    hasRequestedAllocationTransfer,
                    destinationResource: destinationResource
                      ? destinationResource
                      : { ...projectResource, uuid: id },
                    tasks: selectedTasks,
                  })
                }
              >
                {t("removeResourceFromProject.confirm")}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </Context.Provider>
  );
}

export { RemoveResourceFromProject };
